import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Store, TrendingUp, Users, Package, ArrowUpRight, Download, IndianRupee, Copy, Share2, Check, FileArchive } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PartnerDashboardPage() {
  const { user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const [s, o, r] = await Promise.all([
          axios.get(`${API}/partner/stats`, { headers }),
          axios.get(`${API}/partner/orders`, { headers }),
          axios.get(`${API}/partner/referral`, { headers }),
        ]);
        setStats(s.data); setOrders(o.data); setReferral(r.data);
      } catch (e) { console.error(e); }
    })();
  }, []);

  const copyLink = async () => {
    if (!referral?.share_url) return;
    try {
      await navigator.clipboard.writeText(referral.share_url);
      setCopied(true);
      toast({ title: 'Link copied' });
      setTimeout(() => setCopied(false), 2000);
    } catch { toast({ title: 'Copy failed' }); }
  };

  const shareNative = async () => {
    if (!navigator.share || !referral?.share_url) { copyLink(); return; }
    try {
      await navigator.share({
        title: 'Connect Near Me · Buy your Connect tag',
        text: `Get your Connect Near Me tag through my link and save time and money.`,
        url: referral.share_url,
      });
    } catch {}
  };

  return (
    <Layout>
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <p className="eyebrow">Partner console</p>
              <h1 className="h-display text-3xl md:text-4xl mt-1"><em className="font-serif-display italic font-normal text-navy">{user?.business_name || user?.name}</em></h1>
              <p className="text-sm text-neutral-500 mt-1 capitalize">{user?.partner_type} · {user?.city}</p>
            </div>
            <div className="flex gap-2">
              <button className="btn-outline text-sm"><Download size={14} /> Marketing kit</button>
              <button className="btn-primary text-sm"><Package size={14} /> Order bulk stock</button>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
            <PStat icon={Package} label="Tags sold" value={stats?.tags_sold || 0} />
            <PStat icon={IndianRupee} label="Revenue" value={`₹${stats?.revenue || 0}`} />
            <PStat icon={TrendingUp} label="Commission" value={`₹${stats?.commission || 0}`} />
            <PStat icon={Users} label="Customers" value={stats?.customers || 0} />
          </div>

          {/* Referral share */}
          {referral && (
            <div className="mt-8 card-soft p-6 bg-navy text-white border-navy">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-blue-200">Your referral code</p>
                  <p className="h-display text-3xl mt-1">{referral.referral_code}</p>
                  <p className="text-xs text-blue-200 mt-1">Every sale via your link earns you commission automatically.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 bg-white/10 rounded-full px-4 py-2 text-xs text-blue-100 truncate min-w-0 max-w-md">
                    {referral.share_url}
                  </div>
                  <button onClick={copyLink} className="inline-flex items-center gap-2 rounded-full bg-white text-navy px-4 py-2 text-sm font-semibold hover:bg-blue-50 transition-colors">
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
                  </button>
                  <button onClick={shareNative} className="inline-flex items-center gap-2 rounded-full border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:bg-white/10 transition-colors">
                    <Share2 size={14} /> Share
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="mt-10 card-soft p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-semibold text-lg">Recent activity</h2>
              <button className="text-xs text-navy font-semibold">View all</button>
            </div>
            {orders.length === 0 && <p className="text-sm text-neutral-500 py-6 text-center">No orders yet. Your bulk orders and customer sales will show here.</p>}
            {orders.map((o) => (
              <div key={o.id} className="flex justify-between py-3 border-b last:border-0">
                <div>
                  <p className="text-sm font-medium">{o.customer_name}</p>
                  <p className="text-xs text-neutral-500">{o.items?.map((i) => `${i.name} x${i.qty}`).join(', ')}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-navy text-sm">&#8377;{o.total}</p>
                  <p className="text-[10px] text-neutral-500 uppercase">{o.status}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <BulkInvoiceCard headers={headers} />
            <div className="card-soft p-6">
              <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center mb-4"><Store size={20} /></div>
              <h3 className="font-semibold">Partner support</h3>
              <p className="text-sm text-neutral-500 mt-1">Your dedicated relationship manager is available Mon–Sat, 9 AM to 8 PM.</p>
              <div className="mt-4 flex gap-3">
                <a href="tel:1800XXXXXXX" className="btn-outline text-sm">Call RM</a>
                <a href="mailto:partners@connectnearme.in" className="btn-ghost text-sm">Email</a>
              </div>
            </div>
            <div className="card-soft p-6 bg-navy text-white border-navy">
              <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center mb-4"><TrendingUp size={20} /></div>
              <h3 className="font-semibold">Grow with campaigns</h3>
              <p className="text-sm text-blue-100 mt-1">Boost your commissions with monthly co-branded promos in your city.</p>
              <button className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-navy px-4 py-2 text-sm font-semibold">See campaigns <ArrowUpRight size={14} /></button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

function PStat({ icon: Icon, label, value }) {
  return (
    <div className="card-soft p-4">
      <div className="flex items-center gap-2 text-neutral-500 text-xs"><Icon size={14} /> {label}</div>
      <p className="h-display text-3xl mt-2">{value}</p>
    </div>
  );
}

function BulkInvoiceCard({ headers }) {
  const today = new Date().toISOString().slice(0, 10);
  const monthStart = new Date();
  monthStart.setDate(1);
  const [from, setFrom] = useState(monthStart.toISOString().slice(0, 10));
  const [to, setTo] = useState(today);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/partner/invoices/bulk.zip`, {
        params: { from_date: from, to_date: to },
        headers,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `invoices-${from}-to-${to}.zip`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
      toast({ title: 'ZIP downloaded' });
    } catch (e) {
      toast({ title: 'Download failed', description: e.response?.data?.detail || 'No paid invoices in this range.' });
    } finally { setBusy(false); }
  };

  return (
    <div className="card-soft p-6">
      <div className="w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center mb-4"><FileArchive size={20} /></div>
      <h3 className="font-semibold">Bulk invoice download</h3>
      <p className="text-sm text-neutral-500 mt-1">Get all invoices for orders placed via your referral, in one ZIP with a summary CSV. Perfect for monthly accounting.</p>
      <div className="mt-4 grid grid-cols-2 gap-2">
        <div><label className="text-xs">From</label><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" /></div>
        <div><label className="text-xs">To</label><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 w-full px-3 py-2 rounded-lg border border-black/15 text-sm" /></div>
      </div>
      <button disabled={busy} onClick={download} className="btn-primary w-full justify-center mt-4 disabled:opacity-60">
        <Download size={14} /> {busy ? 'Preparing…' : 'Download ZIP'}
      </button>
    </div>
  );
}
